/**
 * steps/FeeConfigurationChoiceStep.tsx
 * Fee configuration choice step for the onboarding wizard
 * 
 * Presents users with the choice between using standard broker fees
 * or customizing their fee structure. Integrates with existing
 * wizard navigation and data management systems.
 */

// 1. External dependencies
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {  DollarSign, Settings, CheckCircle, AlertCircle, Plus, Minus } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardBody, CardHeader, CardFooter, Button, Alert, Skeleton, RadioGroup, Radio, Accordion, AccordionItem } from '@enfomentis/ui';
import { useBrokerFeeDetails } from '@/hooks/useBrokerFeeDetails';
import type { BrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';
import type { AssetClassType, MarketSegment } from '@/types/shared/AssetClass';
import { MARKET_SEGMENT_DISPLAY_MAP } from '@/constants/assetClass';
import { formatMarketSegmentDisplay } from '@/utils/assetClass';
import { BROKER_FEE_CATALOG_DATA } from '@/data/brokerFeeCatalog';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

// Fee formatting interfaces and types (from FeeDetailModal)
interface FeeDisplayConfig {
  showCurrency?: boolean;
  showRoundTripIndicator?: boolean;
  compact?: boolean;
}

interface FormattedFee {
  amount: string;
  label: string;
  description: string;
  isPercentage: boolean;
  isSpreadOnly: boolean;
  overnightFinancing: {
    benchmark: string;
    spread: string;
    frequency: string;
  } | undefined;
}

interface AssetClassSection {
  assetClass: string;
  title: string;
  count: number;
  marketSegmentGroups: MarketSegmentGroup[];
}

interface MarketSegmentGroup {
  marketSegment: string;
  title: string;
  fees: BrokerFeeConfiguration[];
}

// Helper functions for fee formatting (adapted from FeeDetailModal)
const formatFee = (fee: BrokerFeeConfiguration, _config: FeeDisplayConfig = {}): FormattedFee => {
  const categoryName = fee.name || 'Unknown Fee';
    
  const commission = fee.commission;
  const isSpreadOnly = commission.feeType === 'spreadOnly';
  const isPercentage = commission.feeType === 'percentage';
  
  let amount = 'Fee varies';
  if (isSpreadOnly) {
    amount = 'Spread only';
  } else if (commission.feeAmount) {
    const precision = commission.feePrecision || 2;
    if (isPercentage) {
      const percent = commission.feeAmount / 100;
      amount = `${percent.toFixed(2)}%`;
    } else {
      const dollars = commission.feeAmount / Math.pow(10, precision);
      amount = `$${dollars.toFixed(2)}`;
    }
  }

  // Format overnight financing if present
  let overnightFinancing: { benchmark: string; spread: string; frequency: string; } | undefined;
  if (fee.overnightFinancing) {
    const financing = fee.overnightFinancing;
    const spreadPrecision = financing.adminSpreadPrecision || 2;
    const spreadValue = financing.adminSpread / Math.pow(10, spreadPrecision);
    
    overnightFinancing = {
      benchmark: financing.benchmark || 'Market Rate',
      spread: `${spreadValue.toFixed(2)}%`,
      frequency: financing.frequency || 'daily'
    };
  }
    
  return {
    amount,
    label: categoryName,
    description: fee.notes || 'Fee details',
    isPercentage,
    isSpreadOnly,
    overnightFinancing
  };
};

const sortFeesByPriority = (fees: BrokerFeeConfiguration[]): BrokerFeeConfiguration[] => {
  return [...fees].sort((a, b) => {
    // Sort by name for consistent ordering
    return a.name.localeCompare(b.name);
  });
};

/**
 * Fee configuration choice step component
 */
export const FeeConfigurationChoiceStep: React.FC = () => {
  const { draft, setFeeConfigurationChoice } = useOnboardingData();
  const selectedBrokerId = draft?.account?.selectedBrokerId;

  // Load broker fee details
  const { data: feeDetails, loading, error } = useBrokerFeeDetails(selectedBrokerId || null);

  // Pre-load catalog data for next step to eliminate flash
  useEffect(() => {
    if (selectedBrokerId) {
      // Touch the catalog data to ensure it's loaded/cached for the next step
      BROKER_FEE_CATALOG_DATA.get(selectedBrokerId);
    }
  }, [selectedBrokerId]);

  // Local state for user interaction
  const [selectedChoice, setSelectedChoice] = useState<'standard' | 'custom'>('standard'); // Default to standard
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Organize fees by asset class (adapted from FeeDetailModal)
  const assetClassSections = useMemo((): AssetClassSection[] => {
    const sections: AssetClassSection[] = [];
    
    if (!feeDetails?.feeConfigurations) {
      return sections;
    }

    // Group fees by asset class
    const assetClassMap = new Map<AssetClassType, BrokerFeeConfiguration[]>();
    feeDetails.feeConfigurations.forEach(config => {
      if (config.category) {
        const assetClass = config.category.assetClass;
        if (!assetClassMap.has(assetClass)) {
          assetClassMap.set(assetClass, []);
        }
        const assetConfigs = assetClassMap.get(assetClass);
        if (assetConfigs) {
          assetConfigs.push(config);
        }
      }
    });

    // Create sections for each asset class
    if (assetClassMap.size > 0) {
      assetClassMap.forEach((configs, assetClass) => {
        // Group by market segment within asset class
        const marketSegmentMap = new Map<MarketSegment, BrokerFeeConfiguration[]>();
        configs.forEach(config => {
          if (config.category) {
            const marketSegment = config.category.marketSegment as MarketSegment;
            if (!marketSegmentMap.has(marketSegment)) {
              marketSegmentMap.set(marketSegment, []);
            }
            const segmentConfigs = marketSegmentMap.get(marketSegment);
            if (segmentConfigs) {
              segmentConfigs.push(config);
            }
          }
        });

        // Create market segment groups
        const marketSegmentGroups: MarketSegmentGroup[] = [];
        marketSegmentMap.forEach((segmentConfigs, marketSegment) => {
          marketSegmentGroups.push({
            marketSegment,
            title: formatMarketSegmentDisplay(marketSegment, MARKET_SEGMENT_DISPLAY_MAP),
            fees: sortFeesByPriority(segmentConfigs)
          });
        });

        // Sort market segment groups by title
        marketSegmentGroups.sort((a, b) => a.title.localeCompare(b.title));

        sections.push({
          assetClass,
          title: assetClass,
          count: configs.length,
          marketSegmentGroups
        });
      });
    }

    // Sort sections by title
    sections.sort((a, b) => a.title.localeCompare(b.title));
    
    return sections;
  }, [feeDetails?.feeConfigurations]);

  // Get broker name from draft or use fallback
  const brokerName = useMemo(() => {
    if (draft?.account?.brokerSnapshot?.brokerName) return draft.account.brokerSnapshot.brokerName;
    return 'Selected Broker';
  }, [draft]);

  /**
   * Handle selection change
   */
  const handleSelectionChange = useCallback((choice: 'standard' | 'custom'): void => {
    setSelectedChoice(choice);
    // Save choice immediately to draft
    setFeeConfigurationChoice(choice, false);
  }, [setFeeConfigurationChoice]);

  // Initialize selection from draft if available
  // Default to 'standard' if no choice has been made yet
  useEffect(() => {
    const savedChoice = draft?.account?.feeConfiguration?.choice;
    if (savedChoice && savedChoice !== 'pending') {
      setSelectedChoice(savedChoice);
    } else {
      // Default to 'standard' and save to draft
      setSelectedChoice('standard');
      setFeeConfigurationChoice('standard', false);
    }
  }, [draft?.account?.feeConfiguration?.choice, setFeeConfigurationChoice]);

  // Handle customize choice for error state
  const handleCustomizeChoice = useCallback((): void => {
    setSelectedChoice('custom');
    setFeeConfigurationChoice('custom', false);
  }, [setFeeConfigurationChoice]);

  // Error state for missing broker selection
  if (!selectedBrokerId) {
    return (
      <div className="max-w-2xl mx-auto">
        <Alert
          color="danger"
          variant="flat"
          title="Missing Broker Selection"
          description="Please go back and select a broker first."
          startContent={<AlertCircle className="w-5 h-5" />}
          className="text-center"
        />
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
        <Card
        radius="none" 
        shadow="none"
        className="h-full max-h-[100dvh] w-full max-w-4xl flex flex-col border-0 px-2">
          <CardHeader className="pb-2">
            <div>
              <h3 className="text-med font-semibold text-text-primary mb-2 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Selected Broker's Fee Schedule
              </h3>
              <p className="text-sm text-text-secondary">
                Loading broker fee information...
              </p>
            </div>
          </CardHeader>
          <CardBody>
            <Card>
              <CardHeader>
                <Skeleton className="w-3/4 rounded-lg">
                  <div className="h-6 rounded-lg bg-default-200"></div>
                </Skeleton>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <Skeleton className="w-full rounded-lg">
                    <div className="h-4 rounded-lg bg-default-200"></div>
                  </Skeleton>
                  <Skeleton className="w-4/5 rounded-lg">
                    <div className="h-4 rounded-lg bg-default-200"></div>
                  </Skeleton>
                  <Skeleton className="w-3/5 rounded-lg">
                    <div className="h-4 rounded-lg bg-default-200"></div>
                  </Skeleton>
                </div>
              </CardBody>
            </Card>
          </CardBody>
        </Card>
    );
  }

  // Error state
  if (error || !feeDetails) {
    return (
      <Card
      radius="none" 
      shadow="none"
      className="h-full max-h-[100dvh] w-full max-w-4xl flex flex-col border-0">
        <CardHeader className="pb-2">
            <h3 className="text-md font-semibold text-text-primary mb-2 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Selected Broker's Fee Schedule
            </h3>
            <p className="text-sm text-text-secondary">
              Below are the known fees for {brokerName}.
            </p>
        </CardHeader>
        <CardBody className="space-y-6">
          <Alert
            color="warning"
            title="Fee Information Unavailable"
            description="We couldn't load the standard fee information for this broker. You can still proceed with custom configuration."
            startContent={<AlertCircle className="w-5 h-5" />}
          />
          
          <Card>
            <CardBody className="text-center py-8">
              <Button
                color="primary"
                size="lg"
                startContent={<Settings className="w-4 h-4" />}
                onPress={handleCustomizeChoice}
              >
                Configure Custom Fees
              </Button>
            </CardBody>
          </Card>
        </CardBody>
      </Card>
      
    );
  }

  return (
    <Card
      radius="none" 
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col border-0">
      <CardHeader className="pb-2 border-b border-default-100">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">
            Fee Configuration Selection
          </h1>
          <p className="hidden sm:block text-sm text-text-primary">
            {brokerName}'s Fee Schedule
          </p>
        </div>
      </CardHeader>
      <CardBody className="space-y-6 h-full max-h-[100dvh] w-full flex flex-col">
        {/* Broker Fee Summary Card */}
        <div className='space-y-4'>
          {assetClassSections.length > 0 ? (
            <Accordion
              variant="shadow"
              className="w-full"
              selectionMode='multiple'
              selectedKeys={expandedKeys}
              onSelectionChange={(keys) => setExpandedKeys(new Set(Array.from(keys) as string[]))}
              isCompact
              disableIndicatorAnimation
              >
              {assetClassSections.map((section) => (
                <AccordionItem
            textValue="Why do we need this?"
                  key={section.assetClass}
                  title={`${section.title.charAt(0).toUpperCase() + section.title.slice(1)} (${section.count})`}
                  indicator={
                    expandedKeys.has(section.assetClass) ?
                      <Minus className="w-4 h-4" /> :
                      <Plus className="w-4 h-4" />
                  }
                  classNames={{
                    indicator: "order-first mr-2"
                  }}
                >
                  <div className="space-y-4">
                    {section.marketSegmentGroups.map((group, groupIndex) => (
                      section.marketSegmentGroups.length > 1 ? (
                        <Card 
                          key={groupIndex} 
                          className="border-l-2 border-primary/30"
                          radius="none" 
                          shadow="none">
                          <CardHeader className="pb-2">
                            <h4 className="text-sm font-medium text-text-primary">
                              {group.title} ({group.fees.length})
                            </h4>
                          </CardHeader>
                          <CardBody className="pt-0 space-y-3">
                            {group.fees.map((fee, feeIndex) => (
                              <FeeDetailCard key={feeIndex} fee={fee}
                                />
                            ))}
                          </CardBody>
                        </Card>
                      ) : (
                        <div key={groupIndex} className="space-y-3">
                          {group.fees.map((fee, feeIndex) => (
                            <FeeDetailCard key={feeIndex} fee={fee} />
                          ))}
                        </div>
                      )
                    ))}
                  </div>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Card className="border border-default-200">
              <CardBody className="text-center py-8">
                <p className="text-sm text-text-secondary">No fee information available for {brokerName}</p>
              </CardBody>
            </Card>
          )}
        </div>
      </CardBody>
      <CardFooter 
        className="border-t border-default-100 p-2 flex-none bg-transparent">
        <div className="w-full space-y-3">
          <div className="text-center">
            <p className="text-sm md:text-base text-text-primary mb-2">
              Choose your fee configuration approach:
            </p>
          </div>
          <div className="flex justify-center">
            <RadioGroup
              orientation="horizontal"
              color="primary"
              size="md"
              value={selectedChoice}
              onValueChange={(value) => handleSelectionChange(value as 'standard' | 'custom')}
              className="gap-6"
            >
              <Card>
                <CardBody>
                  <Radio value="standard" className="flex items-center gap-2 flex-row-reverse">
                    
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <span className='mr-1'>Standard <span className="hidden sm:inline">Fees</span></span>
                    </div>
                  </Radio>
                </CardBody>
              </Card>
              
              <Card>
                <CardBody>
                  <Radio value="custom" className="flex items-center gap-2 flex-row-reverse">
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      <span>Custom <span className="hidden sm:inline">Fees</span></span>
                    </div>
                  </Radio>
                </CardBody>
              </Card>
            </RadioGroup>
          </div>
          <div className="text-center">
            <p className="text-xs md:text-sm text-text-secondary">
              {selectedChoice === 'standard' 
                ? 'Use publicly available rates. Quick setup, good for most users.' 
                : 'Configure exact account terms. Maximum accuracy, more setup.'
              }
            </p>
          </div>
          {/* Context Information Card */}
          {/* <Card shadow="none">
            <CardHeader className="pb-2">
              <h4 className="font-bold text-large">💡 Why do we need this?</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <ul className="text-sm text-text-secondary space-y-1">
                <li>• Accurate fees are essential for precise P&L calculations</li>
                <li>• Different account types often have different fee structures</li>
                <li>• Fee accuracy helps identify the true profitability of trades</li>
                <li>• Standard fees work well for most users and accounts</li>
                <li>• You can always change or update your fee configuration later</li>
              </ul>
            </CardBody>
          </Card> */}
        </div>
      </CardFooter>
    </Card>
    
  );
};

/**
 * Individual fee detail card component (simplified from FeeDetailModal)
 */
interface FeeDetailCardProps {
  fee: BrokerFeeConfiguration;
}

const FeeDetailCard: React.FC<FeeDetailCardProps> = ({ fee }) => {
  const commissionInfo = formatFee(fee);
  
  return (
    <Card 
      className="border border-default-200"
      radius="none"
      shadow="none">
      <CardBody className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h5 className="font-medium text-sm text-text-primary mb-1">
              {commissionInfo.label}
            </h5>
            <p className="text-xs text-text-secondary mb-2">
              {commissionInfo.description}
            </p>
            
            {/* Display overnight financing if present */}
            {commissionInfo.overnightFinancing && (
              <div className="mt-2 pt-2 border-t border-default-100">
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-xs font-medium text-orange-600">Overnight Financing</span>
                </div>
                <div className="text-xs text-text-secondary space-y-1">
                  <div className="flex justify-between">
                    <span>Benchmark:</span>
                    <span className="font-medium">{commissionInfo.overnightFinancing.benchmark}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Admin Spread:</span>
                    <span className="font-medium text-orange-600">
                      {commissionInfo.overnightFinancing.spread}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Frequency:</span>
                    <span className="font-medium capitalize">{commissionInfo.overnightFinancing.frequency}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="flex flex-col items-end gap-1">
              <span className={`text-sm font-medium ${
                commissionInfo.isSpreadOnly ? 'text-blue-600' : 'text-text-primary'
              }`}>
                {commissionInfo.amount}
              </span>
              {commissionInfo.overnightFinancing && (
                <span className="text-xs text-orange-600 font-medium">
                  + Financing
                </span>
              )}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};